import { get_token } from "@/router";
import SettingsAddUserModal from "@/components/SettingsAddUserModal/SettingsAddUserModal.vue";
import SettingsEditUserModal from "@/components/SettingsEditUserModal/SettingsEditUserModal.vue";

const users_url = process.env.VUE_APP_USER_MANAGEMENT_URL || process.env.VUE_APP_VIEW_USER_URL || "";
const deactivate_user_url = process.env.VUE_APP_DEACTIVATE_USER_URL || users_url;
const users_list_url = process.env.VUE_APP_USER_LIST_URL || "";
const create_user_url = process.env.VUE_APP_CREATE_USER_URL || "";
const roles_url = process.env.VUE_APP_ROLES_URL || "";
const client_app = process.env.VUE_APP_CLIENT_APP || "epfdsd";

function get_table_helper() {
    return window.global_data_table || null;
}

function get_settings_user_table_helpers() {
    const selector = ".global_data_table";

    return {
        watch: {},
        methods: {
            initialize_tables_when_ready() {
                this.$nextTick(() => {
                    get_table_helper()?.init_all_tables?.(this.$el, selector);
                });
            },

            destroy_tables() {
                get_table_helper()?.destroy_all_tables?.(this.$el, selector);
            },

            rebuild_tables() {
                this.$nextTick(() => {
                    get_table_helper()?.rebuild_all_tables?.(this.$el, selector);
                });
            }
        }
    };
}

const settings_user_table_helpers = get_settings_user_table_helpers();

export default {
    name: "SettingsUserManagementTab",

    components: {
        SettingsAddUserModal,
        SettingsEditUserModal
    },

    props: {
        user_profile: {
            type: Object,
            default: null
        }
    },

    data() {
        return ({
            users: [],
            role_options: [],
            is_loading: false,
            is_loading_roles: false,
            is_saving: false,
            load_error: "",
            form_error: "",
            save_success: "",
            show_add_user_modal: false,
            show_edit_user_modal: false,
            selected_user: null,
            users_url: users_url,
            users_list_url: users_list_url,
            create_user_url: create_user_url,
            roles_url: roles_url,
            client_app: client_app
        });
    },

    computed: {
        active_users_count() {
            return this.users.filter((user) => user.is_active).length;
        },

        current_user_id() {
            return this.user_profile?.id || null;
        },

        current_user_email() {
            return String(this.user_profile?.email || "").toLowerCase();
        }
    },

    watch: {
        ...(settings_user_table_helpers.watch || {}),
        user_profile: {
            deep: true,
            handler(next_profile) {
                if (next_profile && !this.users.length && !this.is_loading) {
                    this.set_users(this.get_fallback_users());
                }
            }
        }
    },

    async mounted() {
        await this.load_initial_data();
    },

    beforeUnmount() {
        settings_user_table_helpers.beforeUnmount?.call(this);
    },

    methods: {
        ...(settings_user_table_helpers.methods || {}),

        get_auth_headers(include_json_content_type = true) {
            const auth_token = get_token();
            const headers = {
                "Accept": "application/json"
            };

            if (include_json_content_type) {
                headers["Content-Type"] = "application/json";
            }

            if (auth_token) {
                headers.Authorization = `Token ${auth_token}`;
            }

            return headers;
        },

        get_role_name(user) {
            return user?.user_role?.role?.name || "";
        },

        get_role_id(role) {
            return role?.id ?? null;
        },

        get_user_role_id(user) {
            return user?.user_role?.role?.id ?? null;
        },

        get_user_row(user) {
            const role = user?.user_role?.role || user?.role || null;
            const profile = user?.profile || user?.user_profile || {};

            return ({
                id: user?.id ?? "",
                first_name: user?.first_name ?? "",
                last_name: user?.last_name ?? "",
                username: user?.username ?? "",
                email: user?.email ?? "",
                phone_number: profile?.phone_number ?? user?.phone_number ?? "",
                role: typeof role === "string" ? role : role?.name || this.get_role_name(user),
                role_id: typeof role === "object" ? role?.id ?? this.get_user_role_id(user) : this.get_user_role_id(user),
                is_active: user?.is_active !== false,
                raw: user
            });
        },

        extract_collection(response_data, collection_keys = ["data", "results", "users"]) {
            return this.get_direct_collection(response_data)
                || this.get_nested_collection(response_data, collection_keys)
                || [];
        },

        get_direct_collection(response_data) {
            return Array.isArray(response_data) ? response_data : null;
        },

        get_nested_collection(response_data, collection_keys) {
            if (!this.is_plain_object(response_data)) {
                return null;
            }

            return collection_keys.reduce((found_collection, key) => {
                return found_collection || this.get_collection_from_key(response_data, key, collection_keys);
            }, null);
        },

        get_collection_from_key(response_data, key, collection_keys) {
            const value = response_data[String(key)] || response_data[this.capitalize_key(key)];

            if (Array.isArray(value)) {
                return value;
            }

            return this.is_plain_object(value)
                ? this.extract_collection(value, collection_keys)
                : null;
        },

        is_plain_object(value) {
            return Boolean(value) && typeof value === "object";
        },

        capitalize_key(value) {
            return String(value || "").charAt(0).toUpperCase() + String(value || "").slice(1);
        },

        get_fallback_users() {
            if (!this.user_profile) {
                return [];
            }

            return [this.get_user_row(this.user_profile, "current-user")];
        },

        get_fallback_role_options() {
            const current_user_role = this.user_profile?.user_role?.role || null;
            return current_user_role ? [current_user_role] : [];
        },

        get_error_data(data) {
            return data?.errors || data?.detail || data?.error || data?.message || data;
        },

        get_field_error_message(messages) {
            return Array.isArray(messages) ? messages.join(", ") : messages;
        },

        get_error_object_message(error_data) {
            return Object.entries(error_data)
                .map(([field, messages]) => `${field}: ${this.get_field_error_message(messages)}`)
                .join(" | ");
        },

        get_error_message(data, fallback_message = "Unable to complete user request.") {
            const error_data = this.get_error_data(data);

            if (!error_data) {
                return fallback_message;
            }

            if (typeof error_data === "string") {
                return error_data;
            }

            if (Array.isArray(error_data)) {
                return error_data.join(", ");
            }

            if (typeof error_data === "object") {
                return this.get_error_object_message(error_data);
            }

            return fallback_message;
        },

        async get_response_data(response, fallback_message) {
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(this.get_error_message(data, fallback_message));
            }

            return data;
        },

        async load_initial_data() {
            await this.load_roles();
            await this.load_users();
        },

        async load_roles() {
            this.is_loading_roles = true;

            try {
                const auth_token = get_token();

                if (!this.roles_url || !auth_token) {
                    if (!this.roles_url) {
                        console.warn("VUE_APP_ROLES_URL is not configured.");
                    }
                    this.role_options = this.get_fallback_role_options();
                    return;
                }

                const response = await fetch(this.roles_url, {
                    method: "GET",
                    headers: this.get_auth_headers(false)
                });
                const roles_data = await this.get_response_data(response, "Unable to load roles.");
                const roles = this.extract_collection(roles_data, ["data", "results", "roles"]);

                this.role_options = Array.isArray(roles) && roles.length
                    ? roles
                    : this.get_fallback_role_options();
            } catch {
                this.role_options = this.get_fallback_role_options();
            } finally {
                this.is_loading_roles = false;
            }
        },

        async load_users() {
            this.is_loading = true;
            this.load_error = "";

            try {
                const auth_token = get_token();

                if (!this.users_list_url || !auth_token) {
                    this.load_error = !this.users_list_url
                        ? "VUE_APP_USER_LIST_URL is not configured. Showing available local profile data."
                        : "No auth token found. Showing available local profile data.";
                    this.set_users(this.get_fallback_users(), false);
                    return;
                }

                const response = await fetch(this.users_list_url, {
                    method: "GET",
                    headers: this.get_auth_headers(false)
                });
                const users_data = await this.get_response_data(response, "Unable to load users.");
                const users = this.extract_collection(users_data, ["data", "results", "users"]);

                const next_users = Array.isArray(users)
                    ? users.map((user, index) => this.get_user_row(user, `local-${index + 1}`))
                    : [];

                this.set_users(next_users.length ? next_users : this.get_fallback_users(), false);
            } catch (error) {
                this.set_users(this.get_fallback_users(), false);
                this.load_error = `${error.message || "Unable to load users from the server."} Showing available local profile data.`;
            } finally {
                this.is_loading = false;
                this.$nextTick(() => {
                    this.rebuild_tables();
                });
            }
        },

        set_users(next_users, should_rebuild = true) {
            this.destroy_tables();
            this.users = next_users;

            if (should_rebuild) {
                this.$nextTick(() => {
                    this.initialize_tables_when_ready();
                });
            }
        },

        open_add_user() {
            this.form_error = "";
            this.save_success = "";
            this.show_add_user_modal = true;
        },

        open_edit_user(user) {
            this.form_error = "";
            this.save_success = "";
            this.selected_user = user;
            this.show_edit_user_modal = true;
        },

        close_add_user_modal() {
            this.show_add_user_modal = false;
            this.form_error = "";
        },

        close_edit_user_modal() {
            this.show_edit_user_modal = false;
            this.selected_user = null;
            this.form_error = "";
        },

        validate_user_payload(payload) {
            if (!payload.first_name || !payload.last_name || !payload.email) {
                return "First name, last name, and email are required.";
            }

            return "";
        },

        get_user_payload(form_payload) {
            const role_id = form_payload.role_id ?? this.get_role_id(form_payload.role);
            const payload = {
                first_name: form_payload.first_name,
                last_name: form_payload.last_name,
                email: form_payload.email,
                phone_number: form_payload.phone_number ?? "",
                username: form_payload.username ?? form_payload.email,
                is_active: form_payload.is_active === true,
                client_app: this.client_app
            };

            if (role_id) {
                payload.role_id = role_id;
            }

            return payload;
        },

        async add_user(payload) {
            await this.save_user(payload);
        },

        async save_user(form_payload) {
            const validation_error = this.validate_user_payload(form_payload);

            if (validation_error) {
                this.form_error = validation_error;
                return;
            }

            this.is_saving = true;
            this.form_error = "";
            this.save_success = "";
            this.destroy_tables();

            try {
                const payload = this.get_user_payload(form_payload);

                if (this.create_user_url) {
                    const response = await fetch(this.create_user_url, {
                        method: "POST",
                        headers: this.get_auth_headers(true),
                        body: JSON.stringify(payload)
                    });
                    await this.get_response_data(response, "Unable to save user.");
                    await this.load_users();
                } else {
                    this.save_user_locally(form_payload, payload);
                }

                this.save_success = "User added successfully.";
                this.close_add_user_modal();
            } catch (error) {
                this.form_error = error.message || "Unable to save user.";
            } finally {
                this.is_saving = false;
            }
        },

        save_user_locally(form_payload, payload) {
            this.set_users([
                ...this.users,
                {
                    id: `local-${Date.now()}`,
                    first_name: payload.first_name,
                    last_name: payload.last_name,
                    username: payload.username,
                    email: payload.email,
                    phone_number: payload.phone_number,
                    role: form_payload.role?.name || "",
                    role_id: payload.role_id,
                    is_active: payload.is_active
                }
            ]);
        },

        merge_updated_user(updated_user) {
            if (!updated_user?.id) {
                return;
            }

            this.set_users(this.users.map((user) => {
                return String(user.id) === String(updated_user.id)
                    ? { ...user, ...updated_user }
                    : user;
            }));
        },

        async handle_edit_user_saved(updated_user) {
            this.save_success = "User updated successfully.";
            this.close_edit_user_modal();

            this.merge_updated_user(updated_user);
            await this.load_users();
            this.merge_updated_user(updated_user);
        },

        async toggle_user_status(user) {
            if (this.is_current_user(user) && user.is_active) {
                this.load_error = "You cannot deactivate your own account.";
                return;
            }

            this.is_saving = true;
            this.load_error = "";
            this.save_success = "";

            const next_user = {
                ...user,
                is_active: !user.is_active
            };

            try {
                const can_activate = this.users_url && next_user.is_active;
                const can_deactivate = deactivate_user_url && !next_user.is_active;

                if (user.id && (can_activate || can_deactivate)) {
                    const base_url = this.users_url.endsWith("/") ? this.users_url : `${this.users_url}/`;
                    const deactivate_base = deactivate_user_url.endsWith("/") ? deactivate_user_url : `${deactivate_user_url}/`;
                    const endpoint = next_user.is_active
                        ? `${base_url}${user.id}/update/`
                        : `${deactivate_base}${user.id}/deactivate/`;
                    const payload = next_user.is_active
                        ? { is_active: next_user.is_active }
                        : null;

                    const response = await fetch(endpoint, {
                        method: "POST",
                        headers: this.get_auth_headers(true),
                        body: payload ? JSON.stringify(payload) : null
                    });
                    await this.get_response_data(response, "Unable to update user status.");
                    this.users = this.users.map((existing_user) => {
                        return String(existing_user.id) === String(user.id) ? next_user : existing_user;
                    });
                    await this.load_users();
                } else {
                    this.set_users(this.users.map((existing_user) => {
                        return String(existing_user.id) === String(user.id) ? next_user : existing_user;
                    }));
                }

                this.save_success = next_user.is_active ? "User activated." : "User deactivated.";
            } catch (error) {
                this.load_error = error.message || "Unable to update user status.";
            } finally {
                this.is_saving = false;
            }
        },

        get_full_name(user) {
            return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || "User";
        },

        get_initials(user) {
            const first = user.first_name?.charAt(0) || "";
            const last = user.last_name?.charAt(0) || "";
            const fallback = user.email?.charAt(0) || "U";
            return (first + last || fallback).toUpperCase();
        },

        is_current_user(user) {
            const user_id = user?.id || null;
            const user_email = String(user?.email || "").toLowerCase();

            if (this.current_user_id && String(user_id) === String(this.current_user_id)) {
                return true;
            }

            return this.current_user_email ? user_email === this.current_user_email : false;
        }
    }
};
